import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useContext, useRef, Children, isValidElement, useImperativeHandle, useEffect } from "react";
import PropTypes from "prop-types";
import { motion, MotionConfig, AnimatePresence } from "framer-motion";
import { easeQuadOut } from "d3-ease";
import { TabListItem, TabListWrapper, TabListControlBefore, TabListScrollOuter, TabListScrollInner, TabListInner, TabListControlAfter } from "./tab-list.styles.es.js";
import { UtilityButton } from "../utility-button/utility-button.es.js";
import { tabsContext } from "./tabs-context.es.js";
import "@reach/auto-id";
import "lodash/fp";
import "styled-components";
import "../../contexts/display-mode-context/display-mode-context.es.js";
import "../../contexts/global-edit-context/global-edit-context.es.js";
import "../../contexts/toast-context/toast-context.es.js";
import "../../utils/is-valid-ref.es.js";
import { useHorizontalScroll } from "../../hooks/use-horizontal-scroll.es.js";
import "react-use-measure";
import "@juggle/resize-observer";
import { useMounted } from "../../hooks/use-mounted.es.js";
import "@reach/utils";
import "@reach/rect";
import { useScrollIntoView } from "../../hooks/use-scroll-into-view.es.js";
const AnimatedTabListItem = motion(TabListItem);
const propTypes = {
  children: PropTypes.arrayOf(PropTypes.node).isRequired
};
const TabList = forwardRef(({ children, ...restProps }, ref) => {
  const mounted = useMounted();
  const [activeKey, setActiveKey] = useContext(tabsContext);
  const wrapperRef = useRef();
  const scrollRef = useRef();
  const listRef = useRef();
  const activeRef = useRef();
  const {
    atBeginning,
    atEnd,
    onScroll,
    scrollable,
    scrollBack,
    scrollForward
  } = useHorizontalScroll(scrollRef);
  const validChildren = Children.toArray(children).filter(isValidElement);
  const firstTabKey = validChildren.length > 0 ? validChildren[0].props.tabKey : void 0;
  useImperativeHandle(ref, () => listRef.current);
  const scrollIntoView = useScrollIntoView(scrollRef, activeRef, {
    direction: "horizontal",
    offset: 16
  });
  useEffect(() => {
    if (scrollable) {
      scrollIntoView();
    }
  }, [activeKey, scrollIntoView, scrollable]);
  useEffect(() => {
    if (!activeKey && firstTabKey) {
      setActiveKey(firstTabKey);
    }
  }, [activeKey, firstTabKey, setActiveKey]);
  if (Children.count(children) === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs(TabListWrapper, { ref: wrapperRef, children: [
    scrollable && /* @__PURE__ */ jsx(TabListControlBefore, { children: /* @__PURE__ */ jsx(
      UtilityButton,
      {
        disabled: atBeginning,
        icon: "left",
        magnitude: "small",
        onClick: scrollBack
      }
    ) }),
    /* @__PURE__ */ jsx(
      TabListScrollOuter,
      {
        ref: scrollRef,
        onScroll,
        $fadeLeft: !atBeginning,
        $fadeRight: !atEnd,
        $scrollBehavior: mounted ? "smooth" : "auto",
        children: /* @__PURE__ */ jsx(TabListScrollInner, { children: /* @__PURE__ */ jsx(TabListInner, { ref: listRef, ...restProps, children: /* @__PURE__ */ jsx(MotionConfig, { transition: { ease: easeQuadOut, duration: 0.3 }, children: /* @__PURE__ */ jsx(AnimatePresence, { initial: false, children: validChildren.map((item) => /* @__PURE__ */ jsx(
          AnimatedTabListItem,
          {
            role: "presentation",
            style: { overflow: "hidden" },
            initial: { width: scrollable ? "auto" : 0 },
            animate: { width: "auto" },
            exit: { width: 0 },
            onAnimationComplete: () => {
              scrollIntoView();
              if (item.props.onEntered) {
                setTimeout(item.props.onEntered, 750);
              }
            },
            children: /* @__PURE__ */ jsx(
              "div",
              {
                role: "presentation",
                ref: item.props.tabKey === activeKey ? activeRef : void 0,
                children: item
              }
            )
          },
          item.key
        )) }) }) }) })
      }
    ),
    scrollable && /* @__PURE__ */ jsx(TabListControlAfter, { children: /* @__PURE__ */ jsx(
      UtilityButton,
      {
        disabled: atEnd,
        icon: "right",
        magnitude: "small",
        onClick: scrollForward
      }
    ) })
  ] });
});
TabList.propTypes = propTypes;
export {
  TabList
};
